import { BarcodeFormat, BarcodeScanner, LensFacing } from "@capacitor-mlkit/barcode-scanning";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { useEffect } from "react";

import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";

export type Props = {
  facingMode: "front" | "back";
  onResult: (code: string) => void;
  userDeniedCameraPermissions: boolean;
  onUserDeniedCameraPermissions: (value: boolean) => void;
};

export const QRScannerForMobile = ({
  facingMode,
  userDeniedCameraPermissions,
  onResult,
  onUserDeniedCameraPermissions,
}: Props) => {
  const { t } = useTranslation();
  const checkPermissions = async () => {
    const { camera } = await BarcodeScanner.checkPermissions();
    return camera;
  };

  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera;
  };

  useEffect(() => {
    const scanSingleBarcode = async (lensFacing: LensFacing) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (BarcodeScanner as any).addListener(
        "barcodesScanned",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (result: any) => {
          if (result) {
            onResult(result.barcodes[0].displayValue);
          }
        },
      );

      // Listening for barcodeScanned for backward compatibility
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (BarcodeScanner as any).addListener(
        "barcodeScanned",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (result: any) => {
          if (result) {
            onResult(result.barcode.displayValue);
          }
        },
      );

      await BarcodeScanner.startScan({
        lensFacing,
        formats: [BarcodeFormat.QrCode],
      });
    };

    (async () => {
      const currentCameraPermissions = await checkPermissions();
      if (currentCameraPermissions !== "granted" && currentCameraPermissions !== "limited") {
        const requestedCameraPermissions = await requestPermissions();
        if (requestedCameraPermissions !== "granted" && requestedCameraPermissions !== "limited") {
          onUserDeniedCameraPermissions(true);
          return;
        }
      }

      onUserDeniedCameraPermissions(false);

      await scanSingleBarcode(facingMode === "back" ? LensFacing.Back : LensFacing.Front);
    })();

    return () => {
      (async () => {
        await BarcodeScanner.removeAllListeners();
        await BarcodeScanner.stopScan();
      })();
    };
  }, [facingMode, onResult, onUserDeniedCameraPermissions]);

  useEffect(() => {
    document.querySelector("body")?.classList.add("qr-code-scanner-active");

    return () => {
      document.querySelector("body")?.classList.remove("qr-code-scanner-active");

      BarcodeScanner.removeAllListeners();
      BarcodeScanner.stopScan();
    };
  }, []);

  const handleEnableCameraClick = () => {
    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  };

  return userDeniedCameraPermissions ? (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-4">
      <div className="text-center text-bold1 text-secondary">
        {t("qrScannerForMobile.enablePermission")}
      </div>

      <Button onClick={handleEnableCameraClick}>{t("qrScannerForMobile.enableCamera")}</Button>
    </div>
  ) : null;
};
